import { useParams } from "react-router-dom";
import DatasetTableResponse from "datasets/pages/tables/types/interfaces/table";
import queryString from "query-string";
import { KeyedMutator, SWRResponse } from "swr";
import { NumberParam, useQueryParams, withDefault } from "use-query-params";
import { useDebounce } from "~/hooks/useDebounce";
import { useFetcher } from "~/hooks/useFetcher";
import SearchResponse, { TableEntity } from "~/types/interfaces/catalogResponse";

const { VITE_CATALOG_API } = import.meta.env;

export const PAGE_SIZE = 10;

const adaptHit = ({ _source }: { _source: TableEntity }): DatasetTableResponse => ({
  id: _source.id,
  name: _source.name,
  description: _source.description ?? "",
  fullyQualifiedName: _source.fullyQualifiedName,
  database_schema: _source.databaseSchema.name,
  updated_at: new Date(_source.updatedAt).toISOString(),
  columns: _source.columns,
  deleted: false,
});

const useDatasetTables = (searchTerm: string = ""): SWRResponse<DatasetTableResponse[]> => {
  const { datasetId } = useParams();
  const debouncedSearch = useDebounce(searchTerm);
  const [query] = useQueryParams({
    perPage: withDefault(NumberParam, PAGE_SIZE),
    page: withDefault(NumberParam, 0),
  });
  const { page, perPage } = query;

  const qs = queryString.stringify({
    index: "catalog-api-table_search_index",
    dataset_id: datasetId,
    q: debouncedSearch || "*",
    query_filter: JSON.stringify({ query: { match_all: {} } }),
    from: page ? (page - 1) * perPage : 0,
    size: perPage,
  });

  const resp = useFetcher<SearchResponse<TableEntity>>(
    datasetId ? `${VITE_CATALOG_API}/search/query?${qs}` : null,
    { keepPreviousData: true }
  );

  return {
    ...resp,
    data: resp.data?.hits.hits.map(adaptHit),
    isLoading: resp.isLoading || Boolean(searchTerm && searchTerm !== debouncedSearch),
    mutate: resp.mutate as KeyedMutator<any>,
  } as SWRResponse<DatasetTableResponse[]>;
};

export default useDatasetTables;
